import { css } from '@emotion/react';

import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';

import { useCharacterPost } from '@hooks/useCharacterPost';
import camelize from 'camelize';
import { Spacing } from 'chatie-design-system';

import Button from '@components/common/Button';
import Empty from '@components/common/Empty';
import NativeInFeedAd from '@components/common/NativeInFeedAd';
import { PaginationIterator } from '@components/common/Pagination';
import Tab from '@components/common/Tab';
import CharacterPost, {
  PostCardAction,
} from '@components/community/CharacterPost';
import Pad from '@components/layout/Pad';
import ImageModal from '@components/modal/ImageModal';

import { PlayListLoading } from '@pages/play';

import { FeedCodeType, fetchHomeFeeds } from '@api/feed';
import { IChoiceChip } from '@base/outlookModel';
import { Platform, QUERY_KEYS } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { ICharacterPost, IChatCharacter, IEFinderObject } from '@model';
import { useChatPaginationQuery } from '@utils/query';
import { useWebView } from '@utils/webview';

const groundContainerCSS = css`
  display: flex;
  flex-direction: column;
  margin-top: 42px;

  .chat-enter-pad {
    padding: ${Spacing.small}px ${Spacing.medium}px;
  }

  .empty-btn {
    width: 196px;
    height: 48px;
    margin-top: 20px;
  }
`;

type HomeGroundDetailProps = {
  activeTabInTab: string;
  chips: IChoiceChip[];
  handleActionSheet: (
    value: PostCardAction,
    id: number,
    makerId: number,
    characterId: number
  ) => void;
  likePost: (post: ICharacterPost, characterId: number, query?: string) => void;
  handleNavigatePostDetail: (
    characterId: number,
    id: number,
    userId: number
  ) => void;
  handleClickTag: (id: number, name: string) => void;
  handleClickImg: (img: IEFinderObject<'image'>) => void;
};

const HomeGroundDetail = ({
  activeTabInTab,
  chips,
  handleActionSheet,
  likePost,
  handleNavigatePostDetail,
  handleClickTag,
  handleClickImg,
}: HomeGroundDetailProps) => {
  const { platform } = useGlobalContext();
  const { t } = useTranslation();
  const router = useRouter();
  const { web } = useWebView();

  const selectedChip = chips?.find((chip) => chip.label === activeTabInTab);
  const query = selectedChip?.value;
  const feedQuery = useChatPaginationQuery(
    QUERY_KEYS.OUT_LOOK.GROUND_FEED(activeTabInTab, query),
    ({ pageParam }) =>
      fetchHomeFeeds(selectedChip?.key as FeedCodeType, query, pageParam),
    {
      enabled: !!selectedChip,
      suspense: false,
    }
  );

  const handleClickGroundCharacter = (characterId: number) => {
    if (platform === Platform.web) {
      router.push(`/play/characters/${characterId}?tab=community`);
    } else {
      web.openPage(`/play/characters/${characterId}?tab=community`);
    }
  };

  const handleClickEmpty = () => {
    if (selectedChip?.key === 'my_character') {
      if (platform === Platform.web) {
        router.push(`/studio?tab=play`);
      } else {
        web.openPage(`/studio?tab=play`);
      }
    } else {
      if (platform === Platform.web) {
        router.push(`/play/situations/0/chat-room`);
      } else {
        web.openPage(`/play/situations/0/chat-room`);
      }
    }
  };

  return (
    <>
      <div>
        <PaginationIterator
          name="user-post-list"
          query={feedQuery}
          border
          inFeedAd={<NativeInFeedAd unitKey="home_cp_mobile" />}
        >
          {(item) => {
            if (!item.post) {
              return;
            }
            const playerId = item.character?.player?.user_id;
            const characterId = item.character.id;
            const postId = item.post?.id;
            const character = item.character;

            const handleClickActionSheet = (
              value: PostCardAction,
              id: number
            ) => {
              handleActionSheet(value, id, playerId as number, characterId);
            };

            const handleClickPlay = (character: IChatCharacter) => {
              const characterId = character.id;
              handleClickGroundCharacter(characterId);
            };

            const handleClickLike = (post: ICharacterPost) => {
              likePost(post, characterId, query);
            };

            return (
              <Pad
                onClick={() =>
                  handleNavigatePostDetail(
                    characterId,
                    postId as number,
                    playerId
                  )
                }
              >
                <CharacterPost
                  onLike={handleClickLike}
                  프
                  playerId={playerId}
                  showFixedIcon={false}
                  handleClickPlay={handleClickPlay}
                  playButton={true}
                  handleClickImg={handleClickImg}
                  hasFixedAction={false}
                  imageHeight={400}
                  character={character}
                  tags={item?.character?.tags}
                  {...camelize(item.post)}
                  clip
                  handleActionSheet={handleClickActionSheet}
                  handleClickTag={handleClickTag}
                  commentPreview={item.post?.previewComments}
                />
              </Pad>
            );
          }}
        </PaginationIterator>
      </div>

      <Empty query={feedQuery}>
        {selectedChip?.key !== 'my_character' && (
          <>
            <p>{t('아직 플레이한 캐릭터가 없네요.')}</p>
            <p>{t('얼른 시작해볼까요?')}</p>
            <Button
              onClick={handleClickEmpty}
              type={'border'}
              text={t('내 취향 찾으러 가기  🔍')}
              className={'empty-btn'}
            />
          </>
        )}
        {selectedChip?.key === 'my_character' && (
          <>
            <p>{t('캐릭터를 만들거나')}</p>
            <p>{t('내 캐릭터에 그라운드 글을 작성해 보세요!')}</p>
            <Button
              onClick={handleClickEmpty}
              type={'border'}
              text={t('내 플레이로 갈래요 👀')}
              className={'empty-btn'}
            />
          </>
        )}
      </Empty>
    </>
  );
};

export type HomeGroundProps = {
  chips: IChoiceChip[];
};

const HomeGround = ({ chips }: HomeGroundProps) => {
  const { t } = useTranslation();
  const tabInTabs = chips.map((chip) => ({
    label: t(chip.key),
    value: t(chip.label),
  }));
  const queryClient = useQueryClient();
  const [activeTabInTab, setActiveTabInTab] = useState<string>(
    tabInTabs[0]?.value
  );

  const handleChangeTab = (tab: string) => {
    if (tab !== activeTabInTab) {
      setActiveTabInTab(tab);
    } else {
      queryClient.refetchQueries(QUERY_KEYS.OUT_LOOK.GROUND_FEED_ALL);
    }
  };

  const tabInTabsInstance = {
    tabs: tabInTabs,
    activeTab: activeTabInTab,
    onChange: handleChangeTab,
  };

  const {
    handleActionSheet,
    likePost,
    handleNavigatePostDetail,
    handleClickTag,
    handleClickImg,
    closeImageModal,
    isImageModalOpen,
    selectedImage,
  } = useCharacterPost(activeTabInTab);

  useEffect(() => {
    if (chips && chips.length > 0) {
      setActiveTabInTab(chips[0].label);
    }
  }, [chips]);

  if (!chips || chips.length === 0) {
    return <PlayListLoading />;
  }

  return (
    <>
      <div css={groundContainerCSS}>
        <Tab {...tabInTabsInstance} type={'badge'} hideBorder={true} />
        <HomeGroundDetail
          chips={chips}
          activeTabInTab={activeTabInTab}
          handleActionSheet={handleActionSheet}
          likePost={likePost}
          handleNavigatePostDetail={handleNavigatePostDetail}
          handleClickTag={handleClickTag}
          handleClickImg={handleClickImg}
        />
      </div>
      {selectedImage && (
        <ImageModal
          open={isImageModalOpen}
          image={selectedImage}
          onClose={closeImageModal}
        />
      )}
    </>
  );
};

export default HomeGround;
